import httpClient from '../utils/httpClient';

/**
 * Send the conversation history + meetingData to get feedback.
 *
 * @param {Array<{user: string, ai: string}>} conversation
 * @param {Object} meetingData
 * @returns {Promise<Object>} feedback payload from the server
 */
export const submitFeedback = async (meetingId, { conversation, recording = null }) => {
  return httpClient
    .post(`/api/meeting/${meetingId}/feedback`, { conversation, recording })
    .then((r) => r.data);
};

export const uploadMeetingRecording = async (meetingId, { blob, durationSeconds = null }) => {
  const form = new FormData();
  form.append("recording", blob, "meeting-recording.webm");
  if (durationSeconds != null) {
    form.append("durationSeconds", String(durationSeconds));
  }
  const res = await httpClient.post(`/api/meeting/${meetingId}/recording`, form);
  return res?.data?.recording || null;
};

export const getMeetingRecordingBlob = async (meetingId) => {
  const res = await httpClient.get(`/api/meeting/${meetingId}/recording`, {
    responseType: "blob",
  });
  return res?.data || null;
};

export async function requestPracticeAssist(meetingId, payload, signal) {
  const send = () =>
    httpClient.post(`/api/meeting/${meetingId}/assist`, payload, {
      signal,
      timeout: 12000,
    });

  let response;
  try {
    response = await send();
  } catch (error) {
    if (signal?.aborted || (error?.response?.status && error.response.status < 500)) {
      throw error;
    }
    response = await send();
  }

  return {
    turnId: String(response.data?.turnId || payload.turnId),
    category: String(response.data?.category || "clarity"),
    message: String(response.data?.message || "").trim(),
  };
}


export async function createMeeting(payload) {
  const isFormData = typeof FormData !== "undefined" && payload instanceof FormData;

  const res = await httpClient.post("/api/meeting/", payload, {
    headers: isFormData ? undefined : { "Content-Type": "application/json" },
  });

  return res.data.meeting;
}
// api/meetingApi.js
export async function assignMeeting(formData) {
  // IMPORTANT: don't set Content-Type manually; axios will set boundary
  const r = await httpClient.post('/api/meeting/assign', formData);
  return r.data;
}


  export function markMeetingJoined(meetingId) {
    return httpClient.post(`/api/meeting/${meetingId}/joined`);
  }
  
  export function markMeetingStarted(meetingId) {
    return httpClient.post(`/api/meeting/${meetingId}/started`);
  }
  
export async function markMeetingCompleted(meetingId) {
  const { data } = await httpClient.post(`/api/meeting/${meetingId}/completed`);
  return data;
}
  
  // Get org practice meetings
export async function getOrgPracticeMeetings(orgId) {
  const r = await httpClient.get(`/api/meeting/org/${orgId}`);
  return r.data;  ;
}
export async function deletePracticeMeeting(meetingId) {
  const r = await httpClient.delete(`/api/meeting/${meetingId}`);
  return r.data;
}

export async function getPracticeMeetingById(meetingId) {
  const r = await httpClient.get(`/api/meeting/${meetingId}`);
  return r.data;  ;
}

export async function updatePracticeMeeting(meetingId, payload) {
  const isFormData = typeof FormData !== "undefined" && payload instanceof FormData;
  const r = await httpClient.patch(`/api/meeting/${meetingId}`, payload, {
    headers: isFormData ? undefined : { "Content-Type": "application/json" },
  });
  return r?.data?.meeting ?? r?.data;
}

/**
 * Get feedback + training plans for a specific meeting
 * @param {number|string} meetingId
 * @returns {Promise<Object[]>} Array of feedback/training plan objects
 */
export async function getMeetingFeedback(meetingId) {
  if (!meetingId) {
    return Promise.reject(new Error("meetingId is required"));
  }
  const res = await httpClient
    .get(`/api/meeting/${meetingId}/feedback`);
  return res.data;
}


export async function getMeetingConversation(meetingId) {
  const res = await httpClient.get(`/api/meeting/${meetingId}/conversation`);
  return res?.data?.conversation ?? [];
}

export async function generateMeetingFromPrompt(payload) {
  const isFormData = typeof FormData !== "undefined" && payload instanceof FormData;

  const { data } = await httpClient.post("/api/meeting/generate-meeting", payload, {
    headers: isFormData ? undefined : { "Content-Type": "application/json" },
  });

  return data;
}
